import { FC } from 'react'
import { TruncatedTooltip } from 'src/components/TruncatedTooltip'
import { cn } from 'src/utils/cn'
import styles from './topSources.module.scss'
import { SourceShare } from '../interfaces/comparison.interface'

interface TopSourcesProps {
  comparisonLabel: string
  primaryLabel: string
  sources: SourceShare[]
  // Campaign Comparison reuses this section to compare CAMPAIGNS rather than
  // the two periods of one campaign, and has to be able to say so.
  subtitle?: string
}

const COPY = {
  // A period can genuinely have collected nothing. Saying so is the difference
  // between an empty section and a broken one.
  empty:
    'No mentions were collected in this period, so there are no sources to compare.',
  subtitle:
    'How mention share shifted across sources between the selected periods.',
  title: 'Top Sources — Share of Mentions',
}

// Bars grow out from the centre label. The floor keeps a 0.3% source visible as
// a pill rather than a sliver, so the smallest shares can still be read and
// compared side by side.
const MIN_BAR_WIDTH = 28
const MAX_BAR_WIDTH = 470

export const TopSources: FC<TopSourcesProps> = ({
  comparisonLabel,
  primaryLabel,
  sources,
  subtitle,
}) => {
  // One scale for both sides, so a share is the same length whichever period
  // it belongs to.
  const largest = Math.max(
    ...sources.flatMap(({ comparison, primary }) => [comparison, primary]),
    1
  )

  const barWidth = (value: number) =>
    MIN_BAR_WIDTH + (value / largest) * (MAX_BAR_WIDTH - MIN_BAR_WIDTH)

  return (
    <div className={styles.card}>
      <div className={styles.header}>
        <div className={styles.headerText}>
          <h3 className={styles.title}>{COPY.title}</h3>
          <p className={styles.subtitle}>{subtitle ?? COPY.subtitle}</p>
        </div>

        <div className={styles.legend}>
          <span className={styles.legendItem}>
            <span
              className={cn(styles.legendSwatch, styles.legendSwatchPrimary)}
            />
            <TruncatedTooltip text={primaryLabel} />
          </span>

          <span className={styles.legendItem}>
            <span
              className={cn(styles.legendSwatch, styles.legendSwatchComparison)}
            />
            <TruncatedTooltip text={comparisonLabel} />
          </span>
        </div>
      </div>

      {!sources.length && <p className={styles.empty}>{COPY.empty}</p>}

      <div className={styles.rows}>
        {sources.map(({ comparison, name, primary }) => (
          <div className={styles.row} key={name}>
            {/* Both halves are laid out from the centre outwards: the value
                sits above the bar's inner end on each side. */}
            <div className={cn(styles.side, styles.sidePrimary)}>
              <span className={cn(styles.value, styles.valuePrimary)}>
                {primary}%
              </span>
              <span
                className={cn(styles.bar, styles.barPrimary)}
                style={{ width: barWidth(primary) }}
              />
            </div>

            <span className={styles.name}>{name}</span>

            <div className={cn(styles.side, styles.sideComparison)}>
              <span className={cn(styles.value, styles.valueComparison)}>
                {comparison}%
              </span>
              <span
                className={cn(styles.bar, styles.barComparison)}
                style={{ width: barWidth(comparison) }}
              />
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}
